import React, { useEffect, useState } from 'react';
import { FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { TaskTemplates } from '../components/TaskTemplates';
import type { TaskTemplate } from '../packages/api-client/templates-api';
import { createTask, getProjects, type Project } from '../packages/api-client/pm-api';
import { usePageTitle } from '../hooks/usePageTitle';
import { PageHeader } from '../packages/ui';

/**
 * TemplatesPage — reachable wrapper around TaskTemplates.
 *
 * The component has been orphaned since shipping (UAT 4.14): no route,
 * no nav, no way to get to it. This page gives it a home and wires the
 * `Use` action to a project picker → createTask, so templates can
 * actually instantiate tasks.
 */
export function TemplatesPage() {
    usePageTitle('Task templates');

    const [pickerFor, setPickerFor] = useState<TaskTemplate | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectsLoaded, setProjectsLoaded] = useState(false);
    const [creating, setCreating] = useState(false);

    // Lazy-load projects on first Use click so the templates list stays
    // snappy for users who only want to view/create/delete templates.
    useEffect(() => {
        if (!pickerFor || projectsLoaded) return;
        getProjects()
            .then(res => setProjects(res.data ?? []))
            .catch(() => toast.error('Failed to load projects'))
            .finally(() => setProjectsLoaded(true));
    }, [pickerFor, projectsLoaded]);

    async function handleCreate(projectID: string) {
        if (!pickerFor) return;
        setCreating(true);
        try {
            const task = await createTask(projectID, {
                title: pickerFor.name,
                description: pickerFor.description,
                priority: pickerFor.priority as any,
                estimated_hours: pickerFor.estimated_hours,
            });
            toast.success(`Task "${task.title}" created`);
            setPickerFor(null);
        } catch {
            toast.error('Failed to create task from template');
        } finally {
            setCreating(false);
        }
    }

    return (
        <div style={{ maxWidth: 720 }}>
            <PageHeader title="Task templates" subtitle={<>Reusable task structures. Choose <strong>Use</strong> on a template to add it as a task to a project.</>} />

            <TaskTemplates onUse={t => setPickerFor(t)} />

            {pickerFor && (
                <div
                    onClick={() => !creating && setPickerFor(null)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(2, 8, 7, 0.6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-5)', minWidth: 360, maxWidth: 480,
                            border: '1px solid var(--color-border)',
                        }}
                    >
                        <h3 style={{ margin: 0, marginBottom: 'var(--space-2)' }}>Use template: {pickerFor.name}</h3>
                        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
                            Pick the project where the new task should live.
                        </p>

                        {!projectsLoaded && <div className="spinner" />}
                        {projectsLoaded && projects.length === 0 && (
                            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                                You don't have any projects yet. Create one first.
                            </p>
                        )}

                        {projectsLoaded && projects.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: 320, overflowY: 'auto' }}>
                                {projects.map(p => (
                                    <button
                                        key={p.id}
                                        disabled={creating}
                                        onClick={() => handleCreate(p.id)}
                                        style={{
                                            textAlign: 'left', padding: 'var(--space-3)',
                                            background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-md)', cursor: creating ? 'wait' : 'pointer',
                                            color: 'var(--color-text)',
                                        }}
                                    >
                                        <span style={{ marginRight: 8, display: 'inline-flex', verticalAlign: 'middle' }}>{(p as any).icon ?? <FolderOpen size={16} aria-hidden="true" />}</span>
                                        <strong>{p.name}</strong>
                                    </button>
                                ))}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-4)' }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setPickerFor(null)} disabled={creating}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
